import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { CalendarSettings } from '@/types/settings-api';

/**
 * Canonical query-key factory for the per-practice calendar settings
 * list. Same pattern as the other Schedule query keys so the
 * follow-up save mutation can invalidate this slot cleanly.
 */
export function calendarSettingsListQueryKey(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
): readonly [string, string, string | null | undefined, string | null | undefined] {
  return [
    'settings',
    'calendar-settings-list',
    doctorSetupId,
    organizationId,
  ];
}

/**
 * Practitioner calendar settings list — PROD-762 PR-H.
 *
 * GET /api/Doctor/calenderList?practionerId={doctorId}&organizationId={orgId}
 *
 * **Three magic-string traps on this surface (all verbatim per wire)**:
 *
 *   1. Path: `/api/Doctor/calenderList` — MISSPELLED "calender"
 *      (without the second "a"). The correctly-spelled
 *      "/calendarList" returns 404. Same trap on the POST path
 *      `/api/Doctor/CalenderProfile`.
 *
 *   2. Query string: `practionerId` typo (same as the other Schedule
 *      hooks — `useAppointmentProfileList` + `useAppointmentTemplateList`).
 *
 *   3. The response body uses `providerId` — CORRECTLY spelled.
 *      Distinct from the typo'd `practionerId` on this surface's
 *      query string AND from the typo'd `practionerId` on
 *      AppointmentProfile bodies. Yes — three different conventions
 *      on the same Schedule tab. See `CalendarSettings` type for
 *      the doc.
 *
 * Returns a BARE ARRAY `CalendarSettings[]` (one entry per practice
 * the practitioner is associated with) — distinct from the Profile
 * and Template envelopes which wrap the list in
 * `{ request, isDeleted, ... }`.
 *
 * Flutter source: `settings_data_source_impl.dart:310-327`
 * (`getCalendarSettings`).
 */
export function useCalendarSettingsList(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
) {
  return useQuery({
    queryKey: calendarSettingsListQueryKey(doctorSetupId, organizationId),
    queryFn: ({ signal }) => {
      if (!doctorSetupId || !organizationId) {
        throw new Error(
          'useCalendarSettingsList called without doctorSetupId or organizationId',
        );
      }
      const qs = new URLSearchParams({
        practionerId: doctorSetupId, // verbatim typo per wire contract
        organizationId,
      });
      return get<CalendarSettings[]>(
        // `calenderList` MISSPELLED verbatim per backend route. Don't
        // "fix" to `calendarList` — it 404s.
        `/api/Doctor/calenderList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId && !!organizationId,
  });
}
